import { useState, useEffect } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Users, Shield, Trash2, Pencil, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await adminApi.listUsers();
      setUsers(res.data);
    } catch {
      toast.error('Fehler beim Laden der Nutzer');
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminApi.updateUser(userId, { role: newRole });
      toast.success('Rolle geändert');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleActiveToggle = async (userId, active) => {
    try {
      await adminApi.updateUser(userId, { active });
      toast.success(active ? 'Nutzer aktiviert' : 'Nutzer deaktiviert');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await adminApi.deleteUser(deleteUser.id);
      toast.success('Nutzer gelöscht');
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    try {
      await adminApi.updateUser(editUser.id, {
        name: editUser.name,
        email: editUser.email,
        company_name: editUser.company_name,
        phone: editUser.phone,
      });
      toast.success('Nutzerdaten gespeichert');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.company_name || '').toLowerCase().includes(q) || (u.customer_number || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6" /> Nutzerverwaltung
        </h1>
        <p className="text-muted-foreground mt-1">Alle registrierten Nutzer verwalten</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Nutzer ({users.length})</CardTitle>
              <CardDescription>{filtered.length} angezeigt</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="users-search-input" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Laden...</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Firma</TableHead>
                    <TableHead>Kd-Nr.</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Aktiv</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(u => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.email || u.username || '-'}</TableCell>
                      <TableCell className="text-sm">{u.company_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{u.customer_number || '-'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={u.role || 'user'} onValueChange={v => handleRoleChange(u.id, v)}>
                          <SelectTrigger className="h-7 w-24 text-xs" data-testid={`role-select-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="user">Benutzer</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Switch checked={u.active !== false} onCheckedChange={v => handleActiveToggle(u.id, v)} data-testid={`active-switch-${u.id}`} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditUser({ ...u })} data-testid={`edit-user-${u.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Keine Nutzer gefunden</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={v => !v && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nutzer bearbeiten</DialogTitle>
            <DialogDescription>{editUser?.email}</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={editUser.name || ''} onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))} data-testid="edit-user-name" />
                </div>
                <div className="space-y-2">
                  <Label>E-Mail</Label>
                  <Input value={editUser.email || ''} onChange={e => setEditUser(u => ({ ...u, email: e.target.value }))} data-testid="edit-user-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Firma</Label>
                  <Input value={editUser.company_name || ''} onChange={e => setEditUser(u => ({ ...u, company_name: e.target.value }))} data-testid="edit-user-company" />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input value={editUser.phone || ''} onChange={e => setEditUser(u => ({ ...u, phone: e.target.value }))} data-testid="edit-user-phone" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Abbrechen</Button>
            <Button onClick={handleEditSave} data-testid="edit-user-save-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteUser} onOpenChange={v => !v && setDeleteUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nutzer löschen?</DialogTitle>
            <DialogDescription>
              <strong>{deleteUser?.name || deleteUser?.email}</strong> und alle zugehörigen Daten werden unwiderruflich gelöscht.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-user-btn">Endgültig löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
